import { createContext, useContext, useCallback, useRef, useState, type ReactNode } from 'react';

interface FetchingContextValue {
  /** True when at least one resource is being fetched in the background. */
  isFetching: boolean;
  /**
   * Register a fetch operation for a given key.
   * Returns an unregister function to call when the fetch completes.
   */
  registerFetch: (key: string) => () => void;
}

const FetchingContext = createContext<FetchingContextValue>({
  isFetching: false,
  registerFetch: () => () => {},
});

export function FetchingProvider({ children }: { children: ReactNode }) {
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const keysRef = useRef<Set<string>>(new Set());

  const registerFetch = useCallback((key: string) => {
    keysRef.current = new Set(keysRef.current).add(key);
    setKeys(new Set(keysRef.current));

    return () => {
      keysRef.current = new Set(keysRef.current);
      keysRef.current.delete(key);
      setKeys(new Set(keysRef.current));
    };
  }, []);

  return (
    <FetchingContext.Provider value={{ isFetching: keys.size > 0, registerFetch }}>
      {children}
    </FetchingContext.Provider>
  );
}

export function useFetching(): FetchingContextValue {
  return useContext(FetchingContext);
}
